import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ConfirmImportAPI, PreviewImportAPI } from "network/producers";

// Carica la lista vini (PDF, Excel/CSV o link), mostra l'anteprima da correggere e invia le proposte.
// Tutto passa comunque dalla verifica dell'amministratore.
const BADGE = {
  new: { bg: "#e3f1e1", fg: "#285c24" },
  update: { bg: "#f1e7c4", fg: "#7d6209" },
  unchanged: { bg: "#eee", fg: "#666" },
};

export default function ProducerImport({ token, onDone, onError }) {
  const { t } = useTranslation("producers");
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState(null);
  const [picked, setPicked] = useState({});
  const [msg, setMsg] = useState("");

  const read = async () => {
    if (!file && !url.trim()) { setMsg(t("portal.importNeed")); return; }
    setBusy(true);
    setMsg("");
    try {
      const res = await PreviewImportAPI(token, file ? { file } : { url: url.trim() });
      const list = res.data.items || [];
      setItems(list);
      const on = {};
      list.forEach((i) => { on[i.id] = !i.errors.length && i.action !== "unchanged"; });
      setPicked(on);
    } catch (e) {
      setMsg(e?.message || t("portal.importFailed"));
    }
    setBusy(false);
  };

  const edit = (id, key, value) => setItems((list) => list.map((i) => (i.id === id ? { ...i, data: { ...i.data, [key]: value } } : i)));
  const remove = (id) => setItems((list) => list.filter((i) => i.id !== id));
  const chosen = (items || []).filter((i) => picked[i.id] && !i.errors.length && i.action !== "unchanged" && (i.action === "update" || i.data?.name));

  const send = async () => {
    setBusy(true);
    setMsg("");
    try {
      const wines = chosen.map((i) => ({ action: i.action, wine: i.wine?.uuid, data: i.data }));
      const res = await ConfirmImportAPI(token, wines);
      onDone(res.data);
    } catch (e) {
      setMsg(e?.message || t("portal.importFailed"));
      onError?.(e);
    }
    setBusy(false);
  };

  if (items) {
    const count = (a) => items.filter((i) => i.action === a).length;
    return (
      <div className="pp-card">
        <h4>{t("portal.importPreviewTitle")}</h4>
        <p className="pp-sub">{t("portal.importSummary", { n: count("new"), u: count("update"), s: count("unchanged") })}</p>
        {msg && <div className="pp-msg err">{msg}</div>}
        {items.map((i) => {
          const b = BADGE[i.errors.length ? "unchanged" : i.action];
          const label = i.errors.length ? t("portal.importError") : i.action === "new" ? t("portal.importNew") : i.action === "update" ? t("portal.importUpdate") : t("portal.importSame");
          return (
            <div key={i.id} style={{ borderTop: "1px solid #eee8d8", padding: "10px 0" }}>
              <div className="pp-row">
                <label style={{ margin: 0 }}>
                  <input type="checkbox" disabled={!!i.errors.length || i.action === "unchanged"} checked={!!picked[i.id]}
                    onChange={(e) => setPicked((p) => ({ ...p, [i.id]: e.target.checked }))} />{" "}
                  <b>{i.wine?.name || i.data?.name || "—"}</b> {i.wine?.vintage || i.data?.vintage || ""}
                </label>
                <span>
                  <span className="pp-badge" style={{ background: b.bg, color: b.fg }}>{label}</span>{" "}
                  <button className="pp-btn sec" style={{ minHeight: 32, padding: "0 10px" }} onClick={() => remove(i.id)}>{t("portal.importRemove")}</button>
                </span>
              </div>
              {i.row ? <div className="pp-sub">{t("portal.importRow", { row: i.row })}</div> : null}
              {i.errors.map((er) => <div key={er} className="pp-sub" style={{ color: "#8c2a1f" }}>{er}</div>)}
              {i.action === "update" && <div className="pp-sub">{t("portal.importMatches", { name: i.wine.name, vintage: i.wine.vintage || "" })}</div>}
              {!i.errors.length && i.action === "new" && (
                <div className="pp-grid">
                  <div>
                    <label className="pp-lab">{t("portal.importName")}</label>
                    <input className="pp-in" value={i.data.name || ""} onChange={(e) => edit(i.id, "name", e.target.value)} />
                  </div>
                  <div>
                    <label className="pp-lab">{t("portal.importVintage")}</label>
                    <input className="pp-in" inputMode="numeric" value={i.data.vintage ?? ""} onChange={(e) => edit(i.id, "vintage", e.target.value)} />
                  </div>
                </div>
              )}
              {!i.errors.length && i.action !== "unchanged" && (
                <div className="pp-grid">
                  {Object.keys(i.data).filter((k) => k !== "name" && k !== "vintage").map((k) => (
                    <div key={k}>
                      <label className="pp-lab">{t(`portal.fields.${k}`, { defaultValue: k })}</label>
                      <input className="pp-in" value={i.data[k] ?? ""} onChange={(e) => edit(i.id, k, e.target.value)} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        <div className="pp-row" style={{ marginTop: 16 }}>
          <button className="pp-btn sec" disabled={busy} onClick={() => { setItems(null); setMsg(""); }}>{t("portal.importCancel")}</button>
          <button className="pp-btn" disabled={busy || !chosen.length} onClick={send}>
            {busy ? t("portal.sending") : t("portal.importCount", { count: chosen.length })}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pp-card">
      <h4>{t("portal.importTitle")}</h4>
      <p className="pp-sub">{t("portal.importIntro")}</p>
      {msg && <div className="pp-msg err">{msg}</div>}
      <label className="pp-lab">{t("portal.importFile")}</label>
      <input type="file" accept=".pdf,.xlsx,.csv,application/pdf,text/csv" disabled={busy}
        onChange={(e) => { setFile(e.target.files[0] || null); if (e.target.files[0]) setUrl(""); }} />
      <label className="pp-lab">{t("portal.importUrl")}</label>
      <input className="pp-in" type="url" placeholder={t("portal.importUrlPlaceholder")} value={url} disabled={busy}
        onChange={(e) => { setUrl(e.target.value); if (e.target.value) setFile(null); }} />
      <div style={{ marginTop: 14 }}>
        <button className="pp-btn" disabled={busy} onClick={read}>{busy ? t("portal.importReading") : t("portal.importRead")}</button>
      </div>
    </div>
  );
}
